import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { RefreshCcw, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Input,
  Label,
  ToastAction,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";

import { useAuth, useRequestTenantIdMutation, useToast } from "@/hooks";
import { APP_CONSTANTS } from "@/config";
import { httpRequest } from "@/lib";
import { DisplayError } from "@/utils";

const otpSchema = Yup.object().shape({
  code: Yup.string()
    .length(4, "Código deve ter 4 caracteres")
    .required("Código é obrigatório"),
});

const xTenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  password: Yup.string().required("Senha é obrigatória"),
});

export function SignIn() {
  const { t } = useTranslation();

  const { toast } = useToast();

  const { signIn, isAuthenticating } = useAuth();

  const [currentXtenant, setCurrentXtenant] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  const { mutateAsync: requestTenantId, isLoading: isRequestingTenantId } =
    useRequestTenantIdMutation();

  useEffect(() => {
    if (xTenant) {
      setCurrentXtenant(xTenant);
    } else {
      setCurrentXtenant(null);
    }

  }, [isRequestingTenantId, xTenant]);

  const otpFormik = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: otpSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await requestTenantId(
          { code: values.code },
          {
            onSuccess: (tenant: any) => {
              httpRequest.defaults.headers["X-Tenant"] = tenant.id;
              localStorage.setItem(
                APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY,
                tenant.id
              );
              setTimeout(() => {
                setCurrentXtenant(tenant.id);
                resetForm();
              }, 1000);
            },
            onError: (error: any) => {
              toast({
                title: "Ops!",
                description: error.response?.data?.error || "Erro inesperado",
                variant: "destructive",
                action: (
                  <ToastAction
                    altText="Tentar novamente"
                    onClick={() => otpFormik.submitForm()}
                  >
                    Tente novamente
                  </ToastAction>
                ),
              });
            },
          }
        );

      } catch {
        otpFormik.setSubmitting(false);
      }
    },
  });

  const loginFormik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await signIn(values);
        resetForm();
      } catch (error) {
        const messageError = t(DisplayError(error));

        if (messageError === "Subscription is canceled or suspended") {
          setShowAlert(true);
          return;
        }
        toast({
          title: "Erro!",
          description: messageError,
          variant: "destructive",
        });
        loginFormik.setSubmitting(false);
      }
    },
  });

  const handleRetry = (formik: any) => {
    formik.setSubmitting(false);
    formik.submitForm();
  };

  return (
    <div className="w-full max-w-[420px]">
      <img
        width={140}
        height={70}
        src="/media/logos/logo-medium.svg"
        alt="CliniSharp"
        className="mx-auto mb-8 h-auto w-[140px] object-contain lg:hidden"
      />
      <div className="rounded-2xl border border-app-border bg-app-surface p-8 shadow-premium-md">
        <ol className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.12em]">
          <li className={currentXtenant ? "text-app-fg-subtle" : "text-brand"}>1 · Clínica</li>
          <li aria-hidden="true" className="h-px w-6 bg-app-border" />
          <li className={currentXtenant ? "text-brand" : "text-app-fg-subtle"}>2 · Acesso</li>
        </ol>
        <h1 className="font-display text-[1.75rem] font-medium leading-tight tracking-[-0.01em] text-app-fg-strong [text-wrap:balance]">
          {t("welcome")}
        </h1>
        <p className="mt-1.5 text-sm text-app-fg-muted">
          {currentXtenant ? t("clinic.auth.access") : t("clinic.otp.access")}
        </p>

        {!currentXtenant ? (
          <form onSubmit={otpFormik.handleSubmit} className="mt-8 flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-app-fg-muted">
                {t("clinic.otp.code")}
              </label>
              <InputOTP
                maxLength={4}
                value={otpFormik.values.code}
                onChange={(value) => otpFormik.setFieldValue("code", value)}
                onComplete={(value) => otpFormik.setFieldValue("code", value)}
                className="w-full justify-center"
                pattern={REGEXP_ONLY_DIGITS}
              >
                <InputOTPGroup className="w-full justify-between gap-3">
                  {[0, 1, 2, 3].map((index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className="h-16 w-16 rounded-lg border border-app-border bg-app-surface-muted text-2xl font-semibold tabular-nums first:rounded-lg last:rounded-lg"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {otpFormik.touched.code && otpFormik.errors.code && (
                <span className="text-sm text-app-danger">{otpFormik.errors.code}</span>
              )}
            </div>
            <Button
              variant="petrol"
              size="lg"
              type="submit"
              className="w-full"
              isLoading={isRequestingTenantId}
              onClick={() => !isRequestingTenantId && handleRetry(otpFormik)}
            >
              {t("actions.enter")}
            </Button>
          </form>
        ) : (
          <form onSubmit={loginFormik.handleSubmit} noValidate className="mt-8 flex flex-col gap-8">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label className="text-[13px] font-medium text-app-fg-muted">{t("form.email")}</Label>
                <Input
                  type="email"
                  placeholder={t("form.email")}
                  {...loginFormik.getFieldProps("email")}
                  isInvalid={loginFormik.touched.email && loginFormik.errors.email}
                  isValid={loginFormik.touched.email && !loginFormik.errors.email}
                  messageError={loginFormik.errors.email}
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                  <Label className="text-[13px] font-medium text-app-fg-muted">{t("form.password")}</Label>
                  <Link to="/auth/forgot-password" className="text-[13px] font-medium text-brand hover:text-brand-hover">
                    {t("form.forgot-password")}
                  </Link>
                </div>
                <Input
                  type="password"
                  placeholder={t("form.password")}
                  {...loginFormik.getFieldProps("password")}
                  isInvalid={loginFormik.touched.password && loginFormik.errors.password}
                  isValid={loginFormik.touched.password && !loginFormik.errors.password}
                  messageError={loginFormik.errors.password}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <Button
                variant="petrol"
                size="lg"
                type="submit"
                className="w-full"
                disabled={isAuthenticating}
                isLoading={isAuthenticating}
                onClick={() => !isAuthenticating && handleRetry(loginFormik)}
              >
                {t("actions.enter")}
              </Button>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
                  setCurrentXtenant(null);
                  loginFormik.resetForm();
                }}
                className="flex w-full items-center justify-center gap-2 text-[13px] text-app-fg-muted transition-colors hover:text-brand"
              >
                <RefreshCcw size={14} aria-hidden="true" />
                {t("form.change-account")}
              </button>
            </div>
          </form>
        )}
      </div>
      <SuspendedAccountModal showAlert={showAlert} setShowAlert={setShowAlert} />
    </div>
  );
}

const SuspendedAccountModal = ({
  showAlert,
  setShowAlert,
}: {
  showAlert: boolean;
  setShowAlert: (open: boolean) => void;
}) => {
  return (
    <Dialog open={showAlert} onOpenChange={setShowAlert}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-app-danger">
            <TriangleAlert />
            Conta bloqueada
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-app-fg">
            assinatura bloqueada. Em caso de dúvidas ou para mais detalhes,
            entre em contato com nosso time de suporte.
          </p>

          <div className="rounded-lg bg-brand-soft p-4">
            <h3 className="mb-2 flex items-center gap-2 font-medium text-brand">
              Contato do suporte
            </h3>
            <ul className="space-y-1 text-sm text-brand">
              <li>
                Email:{" "}
                <span className="font-medium">suporte@clinisharp.com</span>
              </li>
              <li>
                Telefone:{" "}
                <span className="font-medium">+55 (11) 1234-5678</span>
              </li>
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowAlert(false)}>
              Fechar
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                (window.location.href = "mailto:suporte@clinisharp.com")
              }
            >
              Enviar e-mail
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
